<template>
  <div>
    <div style="display: flex; justify-content: flex-end; margin-bottom: 16px;">
      <el-button type="primary" size="small" @click="openNewEventDialog">
        New Event
      </el-button>
    </div>

    <empty-state
      v-if="!eventList || eventList.length === 0"
      message="No events yet"
      description="Click 'New Event' to create your first club event!"
      style="margin-top: 20px"
    ></empty-state>

    <el-table
      v-else
      :data="eventList"
      style="margin-top: 20px"
      :highlight-current-row="false"
      :highlight-selection-row="false"
      :header-cell-class-name="'custom-head-cell'"
      :row-class-name="'custom-row-bg'"
    >
      <el-table-column prop="title" label="Event Name">
      </el-table-column>
      <el-table-column prop="date" label="Date">
        <template slot-scope="scope">
          {{ formatDate(scope.row.date) }}
        </template>
      </el-table-column>
      <el-table-column prop="location" label="Location">
      </el-table-column>
      <el-table-column prop="status" label="Status">
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.status)">
            {{ scope.row.status }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Operation" width="200px">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="handleEdit(scope.row)">
            Edit
          </el-button>
          <el-button type="text" size="small" @click="handleDelete(scope.row)">
            Delete
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Event Dialog -->
    <el-dialog
      :title="isEditing ? 'Edit Event' : 'New Event'"
      :visible.sync="dialogVisible"
      width="50%"
    >
      <el-form :model="eventForm" :rules="rules" ref="eventForm" label-width="100px">
        <el-form-item label="Title" prop="title">
          <el-input v-model="eventForm.title"></el-input>
        </el-form-item>
        
        <el-form-item label="Date" prop="date">
          <el-date-picker
            v-model="eventForm.date"
            type="datetime"
            placeholder="Select date and time"
            style="width: 100%"
          >
          </el-date-picker>
        </el-form-item>

        <el-form-item label="Location" prop="location">
          <el-input v-model="eventForm.location"></el-input>
        </el-form-item>

        <el-form-item label="Description" prop="description">
          <el-input
            type="textarea"
            :rows="4"
            v-model="eventForm.description"
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="submitEvent">
          {{ isEditing ? 'Save' : 'Create' }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<!-- ------------------------------------------ script ------------------------------------------ -->
<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getUserType } from '@/utils/store'
import EmptyState from '@/components/EmptyState.vue'

export default {
  name: 'Events',
  components: {
    EmptyState
  },
  props: {
    clubInfo: {
      type: Object,
      default: () => ({})
    },
    club_id: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      eventList: [],
      dialogVisible: false,
      isEditing: false,
      currentUserId: getUserId(),
      eventForm: {
        title: '',
        date: '',
        location: '',
        description: '',
        status: 'upcoming'
      },
      rules: {
        title: [
          { required: true, message: 'Please enter title', trigger: 'blur' },
          { min: 3, max: 50, message: 'Length should be 3 to 50 characters', trigger: 'blur' }
        ],
        date: [
          { required: true, message: 'Please select date', trigger: 'change' }
        ],
        location: [
          { required: true, message: 'Please enter location', trigger: 'blur' }
        ],
        description: [
          { required: true, message: 'Please enter description', trigger: 'blur' }
        ]
      }
    }
  },

  mounted() {
    this.fetchEvents()
  },

  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getStatusType(status) {
      const types = {
        'upcoming': 'primary',
        'ongoing': 'success',
        'completed': 'info',
        'cancelled': 'danger'
      }
      return types[status] || 'info'
    },

    openNewEventDialog() {
      this.isEditing = false
      this.eventForm = {
        title: '',
        date: '',
        location: '',
        description: '',
        status: 'upcoming'
      }
      this.dialogVisible = true
    },

    handleEdit(event) {
      this.isEditing = true
      // Convert timestamp to Date object for the date picker
      this.eventForm = {
        ...event,
        date: new Date(event.date * 1000)
      }
      this.dialogVisible = true
    },

    async handleDelete(event) {
      try {
        await this.$confirm('Are you sure you want to delete this event?', 'Warning', {
          type: 'warning'
        })

        const response = await postRequest('deleteClubEvent', {
          event_id: event._id.$id
        })

        if (response.data.code === 0) {
          this.$message.success('Event deleted successfully')
          this.fetchEvents()
        } else {
          this.$message.error(response.data.message || 'Failed to delete event')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error deleting event:', error)
          this.$message.error('Failed to delete event')
        }
      }
    },

    async submitEvent() {
      try {
        await this.$refs.eventForm.validate()

        // Convert date to timestamp (seconds)
        const dateTimestamp = Math.floor(new Date(this.eventForm.date).getTime() / 1000)

        const response = await postRequest('updateClubEvent', {
          id: this.isEditing && this.eventForm._id ? this.eventForm._id.$id : '',
          club_id: this.club_id,
          user_id: getUserId(),
          user_type: getUserType(),
          title: this.eventForm.title,
          date: dateTimestamp,
          location: this.eventForm.location,
          description: this.eventForm.description,
          status: this.eventForm.status
        })

        if (response.data.code === 0) {
          this.$message.success(`Event ${this.isEditing ? 'updated' : 'created'} successfully`)
          this.dialogVisible = false
          this.fetchEvents()
        } else {
          this.$message.error(response.data.message || 'Failed to save event')
        }
      } catch (error) {
        console.error('Error saving event:', error)
        this.$message.error('Failed to save event')
      }
    },

    async fetchEvents() {
      try {
        const response = await postRequest('getClubEvents', {
          club_id: this.club_id
        })

        if (response.data.code === 0) {
          this.eventList = response.data.data
        } else {
          this.$message.error('Failed to fetch events')
        }
      } catch (error) {
        console.error('Error fetching events:', error)
        this.$message.error('Failed to fetch events')
      }
    }
  }
}
</script>

<!-- ------------------------------------------ style ------------------------------------------ -->
<style scoped lang="scss">
.custom-head-cell {
  background: #FF8833;
  color: white;
  box-sizing: border-box;
  font-family: GravityBold;
}

:deep(.el-table) {
  .el-button--text {
    padding: 0;
    margin-right: 16px;
  }
}
</style> 